import { useEffect, useState } from "react";
import { Button } from "../../ui/Button.js";
import { Input, Label } from "../../ui/Input.js";
import { ModalOverlay } from "../../ui/Modal.js";
import { RichTextEditor } from "./RichTextEditor.js";
import { inboxRequest, type EmailTemplate } from "./inboxApi.js";

type Editing = { id: string | null; name: string; subject: string; html: string };

/**
 * Shared email templates and the signed-in person's own signature. Templates
 * are shared by all admins; each person has their own signature.
 */
export function EmailSettingsModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<"templates" | "signature">("templates");
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  async function loadTemplates() {
    setTemplates(await inboxRequest<EmailTemplate[]>("/templates").catch(() => []));
  }
  useEffect(() => {
    void loadTemplates();
    void inboxRequest<{ html: string }>("/signature").then((data) => setSignature(data.html)).catch(() => {});
  }, []);

  async function run(action: () => Promise<unknown>, okText: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage({ kind: "ok", text: okText });
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "That did not save." });
    } finally {
      setBusy(false);
    }
  }

  function saveTemplate() {
    if (!editing) return;
    const body = JSON.stringify({ name: editing.name, subject: editing.subject, html: editing.html });
    void run(async () => {
      await inboxRequest(editing.id ? `/templates/${editing.id}` : "/templates", {
        method: editing.id ? "PUT" : "POST",
        body,
      });
      setEditing(null);
      await loadTemplates();
    }, "Template saved.");
  }

  const tabClass = (active: boolean) =>
    `focus-ring -mb-px border-b-2 px-4 py-2 text-sm font-medium ${
      active ? "border-accent text-content-primary" : "border-transparent text-content-secondary hover:text-content-primary"
    }`;

  return (
    <ModalOverlay onClose={onClose} ariaLabel="Templates and signature" className="[&>div]:max-w-3xl [&>div]:max-h-[92vh] [&>div]:overflow-y-auto">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-lg font-semibold text-content-primary">Templates &amp; signature</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        <div role="tablist" className="flex gap-1 border-b border-border-subtle">
          <button type="button" role="tab" aria-selected={tab === "templates"} className={tabClass(tab === "templates")} onClick={() => setTab("templates")}>
            Templates
          </button>
          <button type="button" role="tab" aria-selected={tab === "signature"} className={tabClass(tab === "signature")} onClick={() => setTab("signature")}>
            My signature
          </button>
        </div>

        {message && (
          <p
            role={message.kind === "error" ? "alert" : "status"}
            className={`rounded-lg px-4 py-2 text-sm ${
              message.kind === "error"
                ? "border border-status-rejected-border bg-status-rejected-bg text-status-rejected-text"
                : "border border-status-active-border bg-status-active-bg text-status-active-text"
            }`}
          >
            {message.text}
          </p>
        )}

        {tab === "templates" && !editing && (
          <div className="space-y-3">
            <p className="text-sm text-content-secondary">Shared with every admin. Pick one in New email to fill the subject and message.</p>
            {templates === null ? (
              <div role="status" aria-busy="true" className="h-24 animate-pulse rounded-lg bg-skeleton motion-reduce:animate-none">
                <span className="sr-only">Loading templates</span>
              </div>
            ) : templates.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border-default px-4 py-6 text-center text-sm text-content-muted">
                No templates yet. A good first one: the Transamerica change-request email.
              </p>
            ) : (
              <ul className="divide-y divide-border-subtle rounded-lg border border-border-default">
                {templates.map((template) => (
                  <li key={template.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-content-primary">{template.name}</p>
                      <p className="truncate text-xs text-content-muted">{template.subject || "No subject"}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setEditing({ ...template })}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          if (confirmDelete !== template.id) {
                            setConfirmDelete(template.id);
                            return;
                          }
                          setConfirmDelete(null);
                          void run(async () => {
                            await inboxRequest(`/templates/${template.id}`, { method: "DELETE" });
                            await loadTemplates();
                          }, "Template deleted.");
                        }}
                      >
                        {confirmDelete === template.id ? "Click again to delete" : "Delete"}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Button onClick={() => setEditing({ id: null, name: "", subject: "", html: "" })}>New template</Button>
          </div>
        )}

        {tab === "templates" && editing && (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              saveTemplate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Label>
                Name
                <Input className="mt-1.5" value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} required maxLength={120} />
              </Label>
              <Label>
                Subject
                <Input className="mt-1.5" value={editing.subject} onChange={(event) => setEditing({ ...editing, subject: event.target.value })} maxLength={300} />
              </Label>
            </div>
            <RichTextEditor label="Template message" value={editing.html} onChange={(html) => setEditing((current) => (current ? { ...current, html } : current))} placeholder="Write the reusable message" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setEditing(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !editing.name.trim()}>
                {busy ? "Saving…" : "Save template"}
              </Button>
            </div>
          </form>
        )}

        {tab === "signature" && (
          <div className="space-y-4">
            <p className="text-sm text-content-secondary">Only yours. It goes under every email you send from the inbox.</p>
            <RichTextEditor label="Signature" value={signature} onChange={setSignature} placeholder="Name, title, phone" minHeight="6rem" />
            <div className="flex justify-end">
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () => inboxRequest("/signature", { method: "PUT", body: JSON.stringify({ html: signature }) }),
                    "Signature saved."
                  )
                }
              >
                {busy ? "Saving…" : "Save signature"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}
